import { customFetcher } from "../../shared/api/http";
import { getAuthorizedRequestConfig } from "../../shared/api/auth";

/**
 * Service for managing phone-specific telecom tokens.
 * Each phone number gets its own token based on its wallet.
 * Automatically updates the global telecomTokenStore when fetching tokens.
 */
export class TelecomTokenByPhoneService {
  private tokenCache = new Map<string, string>();

  /**
   * Get token for specific phone number from backend
   * @param msisdn - Phone number
   * @param walletId - Wallet ID (required for wallet-only access)
   */
  async fetchTokenForPhone(msisdn: string, walletId: string): Promise<string> {
    const response = await customFetcher<{ token: string }>(
      `/phone-numbers/telecom/token-by-phone?msisdn=${encodeURIComponent(msisdn)}`,
      {
        ...getAuthorizedRequestConfig(),
        getWalletId: () => walletId,
      },
    );
    return response.token;
  }

  /**
   * Get token from cache or fetch if not available
   * Updates the global telecomTokenStore so TelecomApiService uses this token
   * @param msisdn - Phone number
   * @param walletId - Wallet ID (required for wallet-only access)
   */
  async getTokenForPhone(msisdn: string, walletId: string): Promise<string> {
    const cacheKey = `${msisdn}:${walletId}`;
    const cached = this.tokenCache.get(cacheKey);
    if (cached) {
      // Update global token store with cached token
      await this.updateGlobalTokenStore(cached);
      return cached;
    }

    const token = await this.fetchTokenForPhone(msisdn, walletId);
    this.tokenCache.set(cacheKey, token);
    
    // Update global token store with new token
    await this.updateGlobalTokenStore(token);
    
    return token;
  }

  /**
   * Update the global telecomTokenStore
   * This allows TelecomApiService to use the phone-specific token
   */
  private async updateGlobalTokenStore(token: string): Promise<void> {
    try {
      // Lazy load to avoid circular dependency
      const { telecomTokenService } = await import("./index");
      await telecomTokenService.loadToken(); // Ensure store is initialized
      const tokenStore = (telecomTokenService as any).tokenStore;
      if (tokenStore && typeof tokenStore.setToken === 'function') {
        await tokenStore.setToken(token);
      }
    } catch (error) {
      console.error('[TelecomTokenByPhoneService] Failed to update global token store:', error);
    }
  }

  /**
   * Clear cached token for phone (to force refresh)
   * @param msisdn - Phone number
   * @param walletId - Wallet ID (optional, if not provided clears all tokens for this phone)
   */
  clearTokenForPhone(msisdn: string, walletId?: string): void {
    if (walletId) {
      const cacheKey = `${msisdn}:${walletId}`;
      this.tokenCache.delete(cacheKey);
    } else {
      // Clear all tokens for this phone number (for all wallets)
      const keysToDelete: string[] = [];
      this.tokenCache.forEach((_, key) => {
        if (key.startsWith(`${msisdn}:`)) {
          keysToDelete.push(key);
        }
      });
      keysToDelete.forEach(key => this.tokenCache.delete(key));
    }
  }

  /**
   * Clear all cached tokens
   */
  clearAllTokens(): void {
    this.tokenCache.clear();
  }
}

// Singleton instance
export const telecomTokenByPhoneService = new TelecomTokenByPhoneService();

